// Converts a full HTML document string (produced by our statutory document
// builders) into a genuine OOXML .docx file, in the browser, with no
// server round-trip. Word opens the HTML via its native altChunk/HTML
// import filter (the same filter used when saving as .doc), but because the
// output is a real zip/OOXML package, Word does not show the "file format
// doesn't match extension" warning and no "_files" sidecar folder is created
// on save. This also means any mso-element:header / @page mso-header CSS
// already used in our documents is honoured, giving genuine repeating page
// headers.
import JSZip from 'jszip';

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/afchunk.mht" ContentType="message/rfc822"/>
</Types>`;

const RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="/word/document.xml" Id="R09c83fafc067488e" />
</Relationships>`;

const DOCUMENT_XML_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/aFChunk" Target="/word/afchunk.mht" Id="htmlChunk" />
</Relationships>`;

function buildDocumentXml({ width, height, orient, margins }) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    <w:altChunk r:id="htmlChunk" />
    <w:sectPr>
      <w:pgSz w:w="${width}" w:h="${height}" w:orient="${orient}" />
      <w:pgMar w:top="${margins.top}" w:right="${margins.right}" w:bottom="${margins.bottom}" w:left="${margins.left}" w:header="${margins.header}" w:footer="${margins.footer}" w:gutter="${margins.gutter}"/>
    </w:sectPr>
  </w:body>
</w:document>`;
}

function buildMhtDocument(htmlSource) {
  const quotedPrintable = htmlSource.replace(/=/g, '=3D');
  return `MIME-Version: 1.0
Content-Type: multipart/related;
    type="text/html";
    boundary="----=mhtDocumentPart"


------=mhtDocumentPart
Content-Type: text/html;
    charset="utf-8"
Content-Transfer-Encoding: quoted-printable
Content-Location: file:///C:/fake/document.html

${quotedPrintable}

------=mhtDocumentPart--
`;
}

/**
 * Wraps a full HTML document (as produced by our build*StatutoryDoc functions)
 * into a real .docx Blob. Returns a Promise<Blob>.
 */
export async function htmlToDocxBlob(htmlSource, options = {}) {
  const margins = Object.assign(
    { top: 1440, right: 1440, bottom: 1440, left: 1440, header: 720, footer: 720, gutter: 0 },
    options.margins || {}
  );
  const orientation = options.orientation === 'landscape'
    ? { width: 15840, height: 12240, orient: 'landscape' }
    : { width: 12240, height: 15840, orient: 'portrait' };

  const zip = new JSZip();
  zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
  zip.folder('_rels').file('.rels', RELS_XML);
  const wordFolder = zip.folder('word');
  wordFolder.file('document.xml', buildDocumentXml({ ...orientation, margins }));
  wordFolder.file('afchunk.mht', buildMhtDocument(htmlSource));
  wordFolder.folder('_rels').file('document.xml.rels', DOCUMENT_XML_RELS);

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

/**
 * Builds a .docx from an HTML document string and triggers a browser download.
 * filename should be WITHOUT extension.
 */
export async function downloadHtmlAsDocx(filename, htmlSource, options = {}) {
  const blob = await htmlToDocxBlob(htmlSource, options);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
